import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import { useQueryClient } from "react-query";

import { FullScreenLoader } from "components/Loader";
import AnswerContextProvider, {
  AnswerContextValue,
  useAnswerContext,
} from "contexts/answerContext";
import { useCurrentTenant, useLockVersion } from "contexts/currentTenant";
import { useSession } from "contexts/session";
import { usePusherSubscription } from "hooks/pusher";
import useAnswerContextManager from "hooks/useAnswerContextManager";
import useParam from "hooks/useParam";
import invalidations from "queries/invalidations";
import keys from "queries/keys";
import { useProject, useUpdateProject } from "queries/projects";
import { Project } from "types/entities";
import { isPresent } from "utils/func";

interface ProjectAnswerContextProviderProps {
  project: Project;
  isLoading: boolean;
  reload: () => Promise<void>;
  children: ReactNode;
}

const ProjectAnswerContextProvider: React.FC<ProjectAnswerContextProviderProps> = ({
  project,
  isLoading,
  reload,
  children,
}) => {
  const mutation = useUpdateProject(project.id);
  const save = useCallback((params: any) => mutation.mutateAsync(params), [mutation]);
  const answerContext = useAnswerContextManager({ data: project, isLoading }, save);
  const { dispatch } = answerContext;

  useEffect(() => {
    dispatch({ type: "updateRecord", record: project });
  }, [project, dispatch]);

  const { user, isLoggedIn } = useSession();
  const projectUser = answerContext?.record?.user;
  const readOnly =
    isPresent(user) && isLoggedIn && !projectUser?.is_guest && projectUser?.id !== user?.id;
  const [nextAction, setNextAction] = useState();
  const memoizedContext: AnswerContextValue<Project> = useMemo(
    () => ({
      ...answerContext,
      reload,
      readOnly,
      nextAction,
      setNextAction,
    }),
    [answerContext, readOnly, nextAction, reload],
  );

  return <AnswerContextProvider value={memoizedContext}>{children}</AnswerContextProvider>;
};

export const AnswerContextWithContext = ({ context, children }) => {
  const answerContext = useAnswerContext();
  const mappedAnswerContext = R.assoc("context", context, answerContext);

  return <AnswerContextProvider value={mappedAnswerContext}>{children}</AnswerContextProvider>;
};

const RootAnswerContextProvider = ({ children }) => {
  const projectID = Number(useParam("projectID")) as number;
  const queryClient = useQueryClient();
  const [resetKey, setResetKey] = useState(0);
  const { version_number: currentVersionNumber } = useCurrentTenant();
  const lockVersion = useLockVersion();
  const { data: project, isFetched: isLoaded, isLoading } = useProject(projectID);
  const projectVersionNumber = project?.version_number;

  useEffect(() => {
    if (projectVersionNumber && projectVersionNumber !== currentVersionNumber) {
      lockVersion(projectVersionNumber);
    }
  }, [projectVersionNumber, currentVersionNumber, lockVersion]);

  useEffect(
    () => () => {
      // unlock version so that non-project pages can use the current version
      lockVersion(null);
    },
    [lockVersion],
  );

  const reload = useCallback(async () => {
    await queryClient.resetQueries(keys.projects(projectID));
    setResetKey((prev) => prev + 1);
  }, [projectID, queryClient]);

  const invalidate = useCallback(() => {
    invalidations.projects(queryClient, projectID);
  }, [projectID, queryClient]);
  const pusherChannel = projectID ? `private-project-${projectID}` : null;

  usePusherSubscription(pusherChannel, "update", invalidate);
  usePusherSubscription(pusherChannel, "log-entry-created", invalidate);

  if (!isLoaded) return <FullScreenLoader label="RootAnswerContextProvider" />;

  return (
    <ProjectAnswerContextProvider
      key={resetKey}
      project={project}
      isLoading={isLoading}
      reload={reload}
    >
      {children}
    </ProjectAnswerContextProvider>
  );
};

export default RootAnswerContextProvider;
